// Moteur de lecture à voix haute (synthèse vocale du navigateur, fr-FR), sans React : la
// synthèse et le constructeur d'énoncé sont injectés (doubles simulés dans les tests).
// Une seule lecture à la fois dans toute l'application ; texte lu par morceaux (Chrome
// interrompt les énoncés longs, ~15 s) ; chaque échec est signalé à l'appelant par
// notify(message, ton) : aucune voix, erreur de synthèse, lecture qui ne démarre pas.

export const CHUNK_MAX = 220;
// Délai sans début de lecture au-delà duquel la lecture est déclarée en échec
// (Chromium sans moteur de synthèse : aucun son et aucun événement)
export const START_TIMEOUT_MS = 6000;

export const SPEECH_MESSAGES = {
  unsupported: 'La lecture à voix haute n’est pas disponible dans ce navigateur.',
  empty: 'Rien à lire dans cette réponse.',
  noVoice: 'Aucune voix de synthèse n’est disponible sur cet appareil : lecture impossible.',
  noFrench: 'Aucune voix française sur cet appareil : lecture avec la voix par défaut.',
  notStarted: 'La lecture vocale n’a pas démarré. Réessayez.',
  failed: 'La lecture vocale a échoué. Réessayez.',
};

// Codes d'erreur de SpeechSynthesisErrorEvent (« canceled » et « interrupted » : arrêt voulu)
const ERROR_MESSAGES = {
  'not-allowed': 'Lecture vocale bloquée par le navigateur : cliquez de nouveau sur « Écouter ».',
  network: 'Lecture vocale impossible : la voix demande une connexion réseau.',
  'synthesis-unavailable': SPEECH_MESSAGES.noVoice,
  'language-unavailable': 'Aucune voix française n’est disponible sur cet appareil.',
  'voice-unavailable': 'La voix française n’est plus disponible. Réessayez.',
  'audio-busy': 'Sortie audio occupée : la lecture vocale a échoué.',
  'audio-hardware': 'Aucune sortie audio disponible : la lecture vocale a échoué.',
  'text-too-long': 'Réponse trop longue pour la lecture vocale.',
};

/** Message affiché pour un code d'erreur de synthèse. */
export function speechErrorMessage(code, { hasVoices = true } = {}) {
  if (!hasVoices) return SPEECH_MESSAGES.noVoice;
  return ERROR_MESSAGES[code] ?? SPEECH_MESSAGES.failed;
}

/** Voix française du système (fr-FR de préférence), ou null. */
export function frenchVoice(voices = []) {
  const lang = (voice) => String(voice?.lang ?? '');
  return voices.find((v) => /^fr[-_]FR$/i.test(lang(v))) ?? voices.find((v) => /^fr(?:[-_]|$)/i.test(lang(v))) ?? null;
}

/** Morceaux d'au plus `max` caractères, coupés en fin de phrase quand c'est possible. */
export function chunkText(text, max = CHUNK_MAX) {
  const sentences = [];
  const end = /[.!?;:…]+(?:\s+|$)/g;
  let start = 0;
  let match;
  while ((match = end.exec(text))) {
    sentences.push(text.slice(start, match.index + match[0].length));
    start = match.index + match[0].length;
  }
  if (start < text.length) sentences.push(text.slice(start));

  const out = [];
  let current = '';
  for (const sentence of sentences) {
    if (current && (current + sentence).length > max) {
      out.push(current.trim());
      current = '';
    }
    current += sentence;
    while (current.length > max) {
      const space = current.lastIndexOf(' ', max);
      const cut = space > 0 ? space : max;
      out.push(current.slice(0, cut).trim());
      current = current.slice(cut);
    }
  }
  if (current.trim()) out.push(current.trim());
  return out.filter(Boolean);
}

/**
 * Lecteur partagé : { supported, speak(id, texte, { notify }), stop(), subscribe, getSnapshot }.
 * getSnapshot() renvoie l'identifiant du message en cours de lecture (ou null).
 */
export function createSpeechEngine({
  synth,
  Utterance,
  startTimeout = START_TIMEOUT_MS,
  schedule = setTimeout,
  cancel = clearTimeout,
} = {}) {
  const supported = !!(synth && typeof synth.speak === 'function' && typeof Utterance === 'function');
  const listeners = new Set();
  let speakingId = null;
  let session = 0;
  let watchdog = null;
  let warnedNoFrench = false;

  const setSpeaking = (id) => {
    if (speakingId === id) return;
    speakingId = id;
    listeners.forEach((listener) => listener());
  };
  const voices = () => {
    try {
      return synth.getVoices?.() ?? [];
    } catch {
      return [];
    }
  };
  const disarm = () => {
    if (watchdog === null) return;
    cancel(watchdog);
    watchdog = null;
  };

  // Chrome charge la liste des voix en différé : le premier appel la demande
  if (supported) voices();

  function stop() {
    if (!supported) return;
    session += 1; // les événements des énoncés annulés sont ignorés
    disarm();
    try {
      synth.cancel();
    } catch {
      // synthèse déjà arrêtée
    }
    setSpeaking(null);
  }

  function speak(id, text, { notify } = {}) {
    const report = (message, tone) => notify?.(message, tone);
    if (!supported) {
      report(SPEECH_MESSAGES.unsupported, 'error');
      return false;
    }
    stop();
    if (!text || !String(text).trim()) {
      report(SPEECH_MESSAGES.empty, 'info');
      return false;
    }

    const current = session;
    const active = () => current === session;
    const available = voices();
    const voice = frenchVoice(available);
    if (available.length && !voice && !warnedNoFrench) {
      warnedNoFrench = true;
      report(SPEECH_MESSAGES.noFrench, 'warning');
    }

    let started = false;
    const fail = (message) => {
      if (!active()) return;
      stop();
      report(message, 'error');
    };
    const finish = () => {
      if (!active()) return;
      disarm();
      setSpeaking(null);
    };

    const parts = chunkText(String(text));
    setSpeaking(id);
    for (const [index, part] of parts.entries()) {
      const utterance = new Utterance(part);
      utterance.lang = 'fr-FR';
      if (voice) utterance.voice = voice;
      utterance.onstart = () => {
        if (!active()) return;
        started = true;
        disarm();
      };
      utterance.onerror = (event) => {
        const code = event?.error;
        // Annulation venue d'ailleurs (système, autre lecture) : fin de lecture, pas une erreur
        if (code === 'canceled' || code === 'interrupted') finish();
        else fail(speechErrorMessage(code, { hasVoices: voices().length > 0 }));
      };
      if (index === parts.length - 1) utterance.onend = finish;
      try {
        synth.speak(utterance);
      } catch {
        fail(SPEECH_MESSAGES.failed);
        return false;
      }
    }

    // Rien n'a démarré : sans voix, la cause est connue ; sinon, la synthèse est bloquée
    watchdog = schedule(() => {
      watchdog = null;
      if (!active() || started || synth.speaking) return;
      fail(voices().length ? SPEECH_MESSAGES.notStarted : SPEECH_MESSAGES.noVoice);
    }, startTimeout);
    return true;
  }

  return {
    supported,
    speak,
    stop,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getSnapshot: () => speakingId,
  };
}
